import React, { useState, useRef, useEffect } from 'react';
import { useContent } from '../../context/ContentContext';
import { AmbienceItem } from '../../types';
import { DEFAULT_AMBIENCE_ITEMS, AmbienceCarousel } from '../public/AmbienceCarousel';
import {
  Images,
  Upload,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  Plus,
  Loader2,
  Check,
  Eye,
  EyeOff,
  Sparkles,
  RotateCcw,
  Edit2,
  X,
  Layers,
  AlertCircle,
  Image as ImageIcon,
} from 'lucide-react';

export const AmbienceManager: React.FC = () => {
  const { data, updateData, saveData, uploadImage, showToast } = useContent();

  const [items, setItems] = useState<AmbienceItem[]>(() => {
    if (Array.isArray(data.ambienceItems) && data.ambienceItems.length > 0) {
      return data.ambienceItems;
    }
    return DEFAULT_AMBIENCE_ITEMS;
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Modal / Form state for Add / Edit
  const [editingItem, setEditingBanner] = useState<AmbienceItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formAlt, setFormAlt] = useState<string>('');
  const [formImageUrl, setFormImageUrl] = useState<string>('');
  const [formIsActive, setFormIsActive] = useState<boolean>(true);
  const [formSortOrder, setFormSortOrder] = useState<number>(1);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep local state in sync when context data updates
  useEffect(() => {
    if (Array.isArray(data.ambienceItems) && data.ambienceItems.length > 0) {
      setItems(data.ambienceItems);
    }
  }, [data.ambienceItems]);

  const handleOpenAddModal = () => {
    const nextOrder = items.length > 0 ? Math.max(...items.map((i) => i.sortOrder || 0)) + 1 : 1;
    setEditingBanner(null);
    setFormAlt('');
    setFormImageUrl('');
    setFormIsActive(true);
    setFormSortOrder(nextOrder);
    setUploadError(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item: AmbienceItem) => {
    setEditingBanner(item);
    setFormAlt(item.alt || '');
    setFormImageUrl(item.imageUrl || '');
    setFormIsActive(item.isActive !== false);
    setFormSortOrder(item.sortOrder || 1);
    setUploadError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBanner(null);
    setUploadError(null);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Format file tidak didukung. Harap upload gambar (JPG/PNG/WEBP).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Ukuran file terlalu besar. Maksimal 10MB.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const uploadedUrl = await uploadImage(file, 'ambience');
      if (uploadedUrl) {
        setFormImageUrl(uploadedUrl);
        showToast('Gambar berhasil diupload ke Supabase Storage!', 'success');
      } else {
        setUploadError('Gagal mengupload gambar ke Supabase Storage.');
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Terjadi kesalahan saat upload gambar.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formImageUrl.trim()) {
      setUploadError('URL / Foto Ambience wajib diisi.');
      return;
    }

    let updatedList: AmbienceItem[] = [];

    if (editingItem) {
      // Update existing item
      updatedList = items.map((i) =>
        i.id === editingItem.id
          ? {
              ...i,
              imageUrl: formImageUrl.trim(),
              alt: formAlt.trim() || 'Atmosfer LetonKu',
              isActive: formIsActive,
              sortOrder: formSortOrder,
              updatedAt: new Date().toISOString(),
            }
          : i
      );
    } else {
      // Create new item
      const newItem: AmbienceItem = {
        id: `ambience-${Date.now()}`,
        imageUrl: formImageUrl.trim(),
        alt: formAlt.trim() || 'Atmosfer LetonKu',
        isActive: formIsActive,
        sortOrder: formSortOrder,
        createdAt: new Date().toISOString(),
      };
      updatedList = [...items, newItem];
    }

    // Re-sort items by sortOrder
    updatedList.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    setItems(updatedList);
    handleCloseModal();
    await persistItemsToSupabase(updatedList, true);
  };

  const handleToggleActive = async (item: AmbienceItem) => {
    const updatedList = items.map((i) =>
      i.id === item.id ? { ...i, isActive: !i.isActive, updatedAt: new Date().toISOString() } : i
    );
    setItems(updatedList);
    await persistItemsToSupabase(updatedList, true);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Yakin ingin menghapus foto ambience ini dari homepage?')) return;
    const updatedList = items.filter((i) => i.id !== itemId);
    setItems(updatedList);
    await persistItemsToSupabase(updatedList, true);
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const reordered = [...items];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, moved);

    // Update sortOrder values
    const updatedList = reordered.map((item, idx) => ({
      ...item,
      sortOrder: idx + 1,
    }));

    setItems(updatedList);
    await persistItemsToSupabase(updatedList, true);
  };

  const persistItemsToSupabase = async (newList: AmbienceItem[], notify = false) => {
    setIsSaving(true);
    try {
      let success = false;
      if (typeof updateData === 'function') {
        success = await updateData({ ambienceItems: newList });
      } else if (typeof saveData === 'function') {
        success = await saveData({ ...data, ambienceItems: newList });
      }

      if (notify) {
        if (success) {
          showToast('Daftar foto ambience berhasil diperbarui di Supabase!', 'success');
        } else {
          showToast('Perubahan tersimpan di browser local cache.', 'info');
        }
      }
    } catch (err: any) {
      showToast('Gagal menyimpan ke Supabase: ' + (err?.message || 'Error'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20 tracking-wider">
              SUPER ADMIN • HOMEPAGE CMS
            </span>
          </div>
          <h2 className="font-display font-black text-2xl text-white uppercase tracking-tight mt-1">
            FOTO AMBIENCE HOMEPAGE
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Kelola foto yang tampil pada carousel ambience di halaman utama.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleOpenAddModal}
            className="px-5 py-2.5 rounded-xl bg-[#00E5FF] hover:bg-[#3cf0ff] text-slate-950 font-display font-bold text-xs tracking-wider uppercase flex items-center gap-2 shadow-lg shadow-[#00E5FF]/20 cursor-pointer transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Tambah Foto</span>
          </button>
        </div>
      </div>

      {/* Photo List Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between text-xs font-mono text-slate-400">
          <span>Daftar Foto Ambience ({items.length})</span>
          <span className="text-[11px] text-slate-500">Urutan tampil sesuai nomor urut</span>
        </div>

        {items.length === 0 ? (
          <div className="p-12 text-center rounded-2xl border border-dashed border-slate-800 bg-slate-900/40 space-y-3">
            <ImageIcon className="w-10 h-10 text-slate-600 mx-auto" />
            <div className="text-sm font-semibold text-slate-300">Belum ada foto ambience tersimpan</div>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Klik tombol "Tambah Foto" di atas untuk menambahkan foto pertama pada carousel homepage.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((item, index) => (
              <div
                key={item.id || index}
                className={`p-4 rounded-2xl border transition-all flex flex-col justify-between space-y-3 ${
                  item.isActive !== false
                    ? 'bg-slate-900/80 border-slate-800 hover:border-slate-700'
                    : 'bg-slate-950/60 border-slate-900 opacity-60'
                }`}
              >
                {/* Image Preview Header */}
                <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-slate-950 border border-slate-800">
                  <img
                    src={item.imageUrl}
                    alt={item.alt || 'Ambience LetonKu'}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute top-2 left-2 flex items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-slate-950/80 text-white backdrop-blur-md border border-slate-800">
                      #{index + 1}
                    </span>
                    {item.isActive !== false ? (
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 backdrop-blur-md flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        <span>Aktif</span>
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30 backdrop-blur-md">
                        Nonaktif
                      </span>
                    )}
                  </div>
                </div>

                {/* Alt Description & Controls */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-white line-clamp-1">
                    {item.alt || 'Atmosfer LetonKu'}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                    {/* Reorder Up/Down */}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleMove(index, 'up')}
                        disabled={index === 0}
                        title="Geser Ke Atas"
                        className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-30 text-slate-300 transition cursor-pointer"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMove(index, 'down')}
                        disabled={index === items.length - 1}
                        title="Geser Ke Bawah"
                        className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-30 text-slate-300 transition cursor-pointer"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(item)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 transition cursor-pointer ${
                          item.isActive !== false
                            ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                            : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                        }`}
                      >
                        {item.isActive !== false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        <span>{item.isActive !== false ? 'Nonaktifkan' : 'Aktifkan'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleOpenEditModal(item)}
                        className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-[#00E5FF] transition cursor-pointer"
                        title="Edit Foto"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteItem(item.id)}
                        className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition cursor-pointer"
                        title="Hapus Foto"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl relative">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white uppercase tracking-tight">
                {editingItem ? 'Edit Foto Ambience' : 'Tambah Foto Ambience Baru'}
              </h3>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-1 text-slate-400 hover:text-white rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveItem} className="space-y-4">
              {/* Image Preview & Upload Field */}
              <div>
                <label className="block text-xs font-mono font-semibold tracking-wider text-slate-300 uppercase mb-2">
                  File Foto Ambience
                </label>

                {formImageUrl && (
                  <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-slate-950 border border-slate-800 mb-3">
                    <img
                      src={formImageUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold flex items-center gap-2 transition cursor-pointer disabled:opacity-50"
                  >
                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" /> : <Upload className="w-4 h-4 text-[#00E5FF]" />}
                    <span>{isUploading ? 'Mengupload...' : 'Upload dari Perangkat'}</span>
                  </button>
                  <span className="text-xs text-slate-500">atau masukkan URL langsung</span>
                </div>

                <input
                  type="text"
                  placeholder="https://... atau path gambar"
                  value={formImageUrl}
                  onChange={(e) => setFormImageUrl(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:border-[#00E5FF] mt-2 font-mono text-xs"
                />
              </div>

              {/* Alt Description */}
              <div>
                <label className="block text-xs font-mono font-semibold tracking-wider text-slate-300 uppercase mb-2">
                  Deskripsi / Keterangan Gambar (Alt Text)
                </label>
                <input
                  type="text"
                  placeholder="Atmosfer LetonKu..."
                  value={formAlt}
                  onChange={(e) => setFormAlt(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:border-[#00E5FF]"
                />
              </div>

              {/* Active Checkbox */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="form-is-active"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                  className="w-4 h-4 rounded bg-slate-950 border-slate-800 text-[#00E5FF] focus:ring-0 cursor-pointer"
                />
                <label htmlFor="form-is-active" className="text-xs font-semibold text-slate-300 cursor-pointer">
                  Tampilkan foto ini di carousel homepage (Aktif)
                </label>
              </div>

              {uploadError && (
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{uploadError}</span>
                </div>
              )}

              {/* Modal Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSaving || isUploading}
                  className="px-5 py-2 rounded-xl bg-[#00E5FF] hover:bg-[#3cf0ff] text-slate-950 text-xs font-bold tracking-wider uppercase transition cursor-pointer disabled:opacity-50"
                >
                  Simpan Foto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
